/**
 * @file userDisplayName.js
 * @brief Helpers to format user-facing names while keeping username available.
 */

function clean(value) {
    return typeof value === "string" ? value.trim() : "";
}

/**
 * Build user-facing name parts from user fields.
 * @param {Object} user User-like object with username/first_name/last_name.
 * @param {string} fallback Fallback label when no name data is available.
 * @returns {{primaryName: string, secondaryName: string, avatarInitial: string, searchText: string}}
 */
export function getUserDisplayName(user, fallback = "User") {
    const username = clean(user?.username);
    const firstName = clean(user?.first_name);
    const lastName = clean(user?.last_name);

    const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();
    const primaryName = fullName || lastName || firstName || username || fallback;
    const secondaryName =
        username && username.toLowerCase() !== primaryName.toLowerCase()
            ? `@${username}`
            : "";

    const initialSource = primaryName || username || fallback;
    const avatarInitial = (initialSource[0] || "?").toUpperCase();
    const searchText = [primaryName, username, firstName, lastName].filter(Boolean).join(" ").toLowerCase();

    return { primaryName, secondaryName, avatarInitial, searchText };
}

/**
 * Returns a compact label with primary name and optional username.
 * @param {Object} user User-like object with username/first_name/last_name.
 * @param {string} fallback Fallback label when no name data is available.
 * @returns {string}
 */
export function formatUserDisplayName(user, fallback = "User") {
    const { primaryName, secondaryName } = getUserDisplayName(user, fallback);
    return secondaryName ? `${primaryName} (${secondaryName})` : primaryName;
}
